import useApi from "./useApi";

export default function useTracking() {
	const config = useRuntimeConfig();
	const { post } = useApi();
	const { locale } = useI18n();

	function init() {
		const gtmId = config?.public?.gtm;
		if (!gtmId) return;

		if (document.getElementById("gtm-js")) return;

		(function (w, d, s, l, i) {
			w[l] = w[l] || [];
			w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
			var f = d.getElementsByTagName(s)[0],
				j = d.createElement(s),
				dl = l != "dataLayer" ? "&l=" + l : "";
			j.async = true;
			j.src = "//www.googletagmanager.com/gtm.js?id=" + i + dl;
			j.id = "gtm-js";
			f.parentNode.insertBefore(j, f);
		})(window, document, "script", "dataLayer", gtmId);
	}

	function pushPageView(customEvent = null) {
		if (!window.dataLayer) window.dataLayer = [];

		if (customEvent) {
			pushGa4(customEvent);
			return;
		}

		const path = window.location.href.replace(window.location.origin, "");
		pushGa4({
			event: "page_view",
			event_name: "page_view",
			page: {
				page_language: locale.value || "en",
				page_full_uri: path,
				page_step: path
					? path.split("/").slice(1).join("-") + "interactive-program"
					: "interactive-program",
				environment: "interactive program",
				content_name: config?.public?.showName || "unknown",
				content_type: "page",
				content_group: "content",
				device: window.innerWidth > 768 ? "desktop" : "mobile",
			},
			show: {
				business_unit: config?.public?.showDivision || "unknown",
				show_name: config?.public?.showName || "unknown",
				show_type: config?.public?.showType || "unknown",
			},
		});
		return;
	}

	function pushGa4(ga4Events, replaceObj = null) {
		if (!ga4Events) return;
		try {
			const replacedEvents = replacePlaceholder(ga4Events, replaceObj);
			const parsedEvents = cleanJSON(replacedEvents);
			pushDatalayer(parsedEvents);
		} catch (e) {
			console.warn("[useTracking] - ", e);
		}
	}

	function pushDatalayer(datalayerEvent) {
		if (!datalayerEvent) return;
		if (!window.dataLayer) window.dataLayer = [];
		if (Array.isArray(datalayerEvent)) {
			for (let i = 0; i < datalayerEvent.length; i++) {
				window.dataLayer.push(datalayerEvent[i]);
			}
		} else {
			window.dataLayer.push(datalayerEvent);
		}
	}

	function pushSitecoreInteraction(sitecoreEvent, replaceObj = null) {
		if (!sitecoreEvent) return;
		const replacedEvent = replacePlaceholder(sitecoreEvent, replaceObj);
		const parsedEvent = cleanJSON(replacedEvent);

		if (parsedEvent && parsedEvent.ApiUrl) {
			post(parsedEvent.ApiUrl, parsedEvent).catch(() => {
				console.warn(`[useTracking] - Error sending tracking data to Sitecore`);
			});
		}
	}

	function pushAzureInsight(event, properties) {
		if (!event) return;
		if (!window.appInsights) return;

		let props = properties || {};

		var eventTelemetry = {
			name: event,
			properties: props,
		};

		appInsights.trackEvent(eventTelemetry);
	}

	function pushException(ex, properties) {
		if (!ex) return;
		if (!window.appInsights) return;

		let props = properties || {};

		if (!ex.message && props.name) {
			ex.message = props.name;
		}

		var severity;
		if (ex.cause >= 400 && ex.cause < 500) {
			severity = 3;
		} else if (ex.cause >= 500 && ex.cause < 600) {
			severity = 4;
		} else {
			severity = 2;
		}

		// props.frontendVersion = getWindowProperty('version');

		var exceptionTelemetry = {
			exception: ex,
			severityLevel: severity,
			properties: props,
		};

		appInsights.trackException(exceptionTelemetry);
	}

	function pushTracking(events, replaceObj = null) {
		if (!events) return;
		const keys = Object.keys(events);

		for (let i = 0; i < keys.length; ++i) {
			const key = keys[i];
			const event = events[key];

			switch (key) {
				case "ga4":
					pushGa4(event, replaceObj);
					break;
				case "scinteraction":
					pushSitecoreInteraction(event, replaceObj);
					break;
				case "azure":
					pushAzureInsight(event, replaceObj);
					break;
				default:
					break;
			}
		}
	}

	function trackVideo(videoEl) {
		if (!videoEl || !videoEl.src) return;

		window.dataLayer = window.dataLayer || [];

		const videoEvents = {
			play: "start",
			timeupdate: "progress",
			ended: "complete",
		};

		const videoTitle = videoEl.src.split("/").pop().split(".")[0];

		const dataLayerEvent = (video_action, video_percent = null) => {
			return {
				event: video_action,
				video_provider: "Sitecore",
				video_title: videoTitle,
				video_duration: Math.round(videoEl.duration * 100) / 100,
				video_current_time: Math.round(videoEl.currentTime * 100) / 100,
				video_url: videoEl.src,
				video_percent,
			};
		};

		// VIDEO START
		let videoStarted = false;
		videoEl.addEventListener("play", (e) => {
			if (videoStarted) return;
			window.dataLayer.push(dataLayerEvent(videoEvents[e.type]));
			videoStarted = true;
		});

		// VIDEO PROGRESS
		const checkpoints = [10, 25, 50, 75];
		let checkpointsTracked = { 10: false, 25: false, 50: false, 75: false };

		videoEl.addEventListener("timeupdate", (e) => {
			const videoProgress = (videoEl.currentTime / videoEl.duration) * 100;

			for (const checkpoint of checkpoints) {
				if (videoProgress > checkpoint - 1 && videoProgress) {
					if (checkpointsTracked[checkpoint]) continue;
					window.dataLayer.push(
						dataLayerEvent(videoEvents[e.type], checkpoint)
					);
					checkpointsTracked[checkpoint] = true;
				}
			}
		});

		// VIDEO COMPLETE
		videoEl.addEventListener("ended", (e) => {
			videoStarted = false;
			checkpointsTracked = { 10: false, 25: false, 50: false, 75: false };
			window.dataLayer.push(dataLayerEvent(videoEvents[e.type]));
		});
	}

	function cleanJSON(strJSON) {
		if (!strJSON) return;
		if (typeof strJSON !== "string") return strJSON;

		let s = strJSON
			.replace(/\\n/g, "\\n")
			.replace(/\\'/g, "\\'")
			.replace(/\\"/g, '\\"')
			.replace(/\\&/g, "\\&")
			.replace(/\\r/g, "\\r")
			.replace(/\\t/g, "\\t")
			.replace(/\\b/g, "\\b")
			.replace(/\\f/g, "\\f");

		// remove non-printable and other non-valid JSON chars
		s = s.replace(/[\u0000-\u0019]+/g, "");
		return JSON.parse(s);
	}

	function replacePlaceholder(str, obj = null) {
		if (!obj) return str;
		const keys = Object.keys(obj);
		return keys.reduce((acc, key) => acc.replaceAll(key, obj[key]), str);
	}

	return {
		init,
		pushPageView,
		cleanJSON,
		pushDatalayer,
		pushTracking,
		pushGa4,
		pushSitecoreInteraction,
		pushAzureInsight,
		pushException,
		trackVideo,
	};
}
